import {
  DownloadIcon,
  PlusIcon,
  UpdateIcon,
  UploadIcon,
} from '@radix-ui/react-icons';
import { Badge, Box, Code, Flex, Skeleton, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonConfirmationDialog } from 'components/common/dialogs/confirmation';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSimpleFileUploader } from 'components/common/file-uploader';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { CommonTableHoC } from 'components/common/table';
import { ErrorTypeEditorDialog } from 'components/sections/admin-portal/error-types/editor';
import { RequestChangeButton } from 'components/sections/admin-portal/error-types/request-change-button';
import { SectionHeaderActions } from 'components/sections/header/actions';
import env from 'config';
import {
  ErrorTypesContext,
  ErrorTypesProvider,
} from 'contexts/admin/error-types.context';
import { ACTIONS_KEY, TABLES } from 'enums/tables';
import { PaginationID } from 'interfaces/cookies/i-app.cookie';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITablePageable } from 'interfaces/tables/pagination';
import { ITableSortable } from 'interfaces/tables/sorting';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import {
  DEFAULT_ERROR_LEVEL,
  ErrorVisibility,
} from 'lib_ts/enums/errors.enums';
import { TextMime } from 'lib_ts/enums/files.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import {
  DEFAULT_ERROR_TYPE,
  IErrorType,
} from 'lib_ts/interfaces/common/i-error-type';
import { useContext, useRef, useState } from 'react';
import { AdminErrorTypesService } from 'services/admin/error-types.service';
import useMiscStore from 'stores/use-misc-store';

const PAGE_SIZES = TABLES.PAGE_SIZES.MD;

export const ErrorTypesTableHoc = () => (
  <ErrorTypesProvider>
    <ErrorTypesTable />
  </ErrorTypesProvider>
);

const ErrorTypesTable = () => {
  const anyOpenDialogs = useMiscStore((s) => s.anyOpenDialogs);
  const errorsCx = useContext(ErrorTypesContext);

  const fileInputRef = useRef<CommonSimpleFileUploader | null>(null);

  const [editorModel, setEditorModel] = useState<
    Partial<IErrorType> | undefined
  >();
  const [dialogEditor, setDialogEditor] = useState<number | undefined>();
  const [dialogDelete, setDialogDelete] = useState<
    | {
        key: number;
        errorID: string;
        error_id: string;
      }
    | undefined
  >();

  const BASE_COLUMNS: ITypedTableColumn<IErrorType>[] = [
    {
      label: 'common.actions',
      key: ACTIONS_KEY,
      actions: [
        {
          label: 'common.edit',
          onClick: (m) => {
            setEditorModel(m);
            setDialogEditor(Date.now());
          },
        },
        {
          label: 'common.preview',
          color: RADIX.COLOR.SUCCESS,
          onClick: (m) => {
            NotifyHelper.userError({
              ...m,
              level: DEFAULT_ERROR_LEVEL,
            });
          },
        },
        {
          label: 'common.duplicate',
          color: RADIX.COLOR.WARNING,
          onClick: (m) => {
            const cloned: Partial<IErrorType> = {
              ...m,
              _id: undefined,
              errorID: `${m.errorID} (copy)`,
            };
            setEditorModel(cloned);
            setDialogEditor(Date.now());
          },
        },
        {
          label: 'common.delete',
          color: RADIX.COLOR.DANGER,
          invisibleFn: (m) => !!m.deleted,
          onClick: (m) => {
            setDialogDelete({
              key: Date.now(),
              error_id: m._id,
              errorID: m.errorID,
            });
          },
        },
        {
          label: 'Restore',
          color: RADIX.COLOR.WARNING,
          invisibleFn: (m) => !m.deleted,
          onClick: (m) => {
            errorsCx.update({ _id: m._id, deleted: false });
          },
        },
      ],
    },
    {
      label: 'ErrorID',
      key: 'errorID',
    },
    {
      label: 'Type',
      key: 'type',
    },
    {
      label: 'Category',
      key: 'category',
    },
    {
      label: 'Sender',
      key: 'sender',
    },
    {
      label: 'Internal',
      key: 'internal',
      formatFn: (et) => (et.internal ? 'Yes' : 'No'),
    },
    {
      label: 'Visibility',
      key: 'visibility',
      formatFn: (et) =>
        et.visibility ? (
          <Badge
            color={
              et.visibility === ErrorVisibility.Engineering
                ? RADIX.COLOR.WARNING
                : RADIX.COLOR.INFO
            }
          >
            {et.visibility.toUpperCase()}
          </Badge>
        ) : (
          '--'
        ),
    },
    {
      label: 'Owner',
      key: 'department',
    },
    {
      label: 'Status',
      key: 'deleted',
      formatFn: (et) => (et.deleted ? 'Deleted' : 'Active'),
    },
  ];

  const { filtered, filters, options, loading, mergeFilters } = errorsCx;

  if (!filtered) {
    return <Skeleton />;
  }

  const pagination: ITablePageable = {
    paginationID: PaginationID.AdminErrorTypeList,
    total: filtered.length,
    enablePagination: true,
    pageSizes: PAGE_SIZES,
  };

  const sort: ITableSortable = {
    enableSort: true,
    defaultSort: {
      key: 'errorID',
      dir: -1,
    },
  };

  const isFiltering =
    filters.errorIDs.length > 0 || filters.internal !== undefined;

  return (
    <ErrorBoundary identifier="ErrorTypesTable">
      <FlexTableWrapper
        gap={RADIX.FLEX.GAP.SECTION}
        table={
          <CommonTableHoC
            dataTestID="AdminErrorTypes"
            identifier="AdminErrorTypesTable"
            toolbarContent={
              <Flex gap={RADIX.FLEX.GAP.SM}>
                {/* filters */}
                <Box flexGrow="1">
                  <CommonSearchInput
                    id="error-types-errorIDs"
                    placeholder="ErrorID"
                    options={options.errorIDs}
                    values={filters.errorIDs}
                    onChange={(values) => mergeFilters({ errorIDs: values })}
                    multiple
                  />
                </Box>

                <Box flexGrow="1">
                  <CommonSelectInput
                    id="error-types-internal"
                    name="internal"
                    placeholder="Internal"
                    options={[
                      { label: 'Yes', value: 'true' },
                      { label: 'No', value: 'false' },
                    ]}
                    value={filters.internal?.toString()}
                    onOptionalBooleanChange={(v) =>
                      mergeFilters({ internal: v })
                    }
                    optional
                  />
                </Box>

                <Box flexGrow="1">
                  <CommonSelectInput
                    id="error-types-visibility"
                    name="visibility"
                    placeholder="Visibility"
                    options={Object.values(ErrorVisibility).map((m) => ({
                      label: m.toUpperCase(),
                      value: m,
                    }))}
                    value={filters.visibility}
                    onChange={(v) =>
                      mergeFilters({ visibility: v as ErrorVisibility })
                    }
                    optional
                  />
                </Box>

                <Box flexGrow="1">
                  <CommonSelectInput
                    id="error-types-deleted"
                    name="deleted"
                    placeholder="Status"
                    options={[
                      { label: 'Active', value: 'false' },
                      { label: 'Deleted', value: 'true' },
                    ]}
                    value={filters.deleted?.toString()}
                    onOptionalBooleanChange={(v) =>
                      mergeFilters({ deleted: v })
                    }
                    optional
                  />
                </Box>

                <Box>
                  <RequestChangeButton />
                </Box>

                {/* actions menu */}
                <SectionHeaderActions
                  actions={[
                    {
                      label: 'common.create',
                      color: RADIX.COLOR.SUCCESS,
                      prefixIcon: <PlusIcon />,
                      onClick: () => {
                        setEditorModel(undefined);
                        setDialogEditor(Date.now());
                      },
                    },
                    {
                      label: 'common.refresh',
                      prefixIcon: <UpdateIcon />,
                      onClick: errorsCx.refresh,
                    },
                    {
                      group: 'JSON',
                      label: 'common.export',
                      prefixIcon: <DownloadIcon />,
                      disabled: filtered.length === 0,
                      onClick: () => {
                        const types: Partial<IErrorType>[] = filtered.map(
                          (m) => {
                            const o: Partial<IErrorType> = {
                              ...DEFAULT_ERROR_TYPE,
                              ...m,
                            };

                            if (typeof o.resolution === 'string') {
                              o.resolution = (o.resolution as string).split(
                                ','
                              );
                            }

                            if (typeof o.patch === 'string') {
                              o.patch = (o.patch as string).split(',');
                            }

                            delete o._id;
                            delete o._created;
                            delete o._createdby;
                            delete o._changed;
                            delete o._changedby;

                            return o;
                          }
                        );

                        MiscHelper.saveAs(
                          new Blob([JSON.stringify(types, null, 2)]),
                          `error-types.json`
                        );
                      },
                    },
                    {
                      group: 'JSON',
                      label: 'common.import',
                      prefixIcon: <UploadIcon />,
                      invisible: env.production,
                      disabled: !fileInputRef.current,
                      onClick: () => {
                        fileInputRef.current?.handleClick();
                      },
                    },
                  ]}
                />
              </Flex>
            }
            displayColumns={BASE_COLUMNS}
            displayData={filtered}
            loading={loading}
            enableListener={!anyOpenDialogs}
            noDataBody={isFiltering ? 'common.no-matches-msg' : undefined}
            {...pagination}
            {...sort}
            vFlex
          />
        }
      />

      {dialogEditor && (
        <ErrorTypeEditorDialog
          key={dialogEditor}
          errorsCx={errorsCx}
          input={editorModel}
          onClose={() => setDialogEditor(undefined)}
        />
      )}

      {dialogDelete && (
        <CommonConfirmationDialog
          key={dialogDelete.key}
          identifier="ConfirmDeleteErrorType"
          title="Delete Error Type"
          content={
            <Text>
              Are you sure you want to delete the error type{' '}
              <Code>{dialogDelete.errorID}</Code>?
            </Text>
          }
          onClose={() => setDialogDelete(undefined)}
          action={{
            label: 'Delete',
            color: RADIX.COLOR.DANGER,
            onClick: () => {
              if (!dialogDelete.error_id) return;
              errorsCx.update({ _id: dialogDelete.error_id, deleted: true });
            },
          }}
        />
      )}

      <CommonSimpleFileUploader
        ref={fileInputRef}
        id="error-types-uploader"
        acceptedTypes={[TextMime.JSON]}
        onChange={async (files) => {
          const success =
            await AdminErrorTypesService.getInstance().importJSON(files);
          if (success) {
            errorsCx.refresh();
          }
        }}
        hidden
      />
    </ErrorBoundary>
  );
};
